import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * An input with things attached to its ends — a unit, an icon, a button — inside
 * one bordered box, so the whole thing reads and focuses as a single control.
 *
 * The shadcn `input-group` API (`InputGroup`, `InputGroupInput`,
 * `InputGroupAddon align="inline-start" | "inline-end"`, `InputGroupText`,
 * `InputGroupButton`), rewritten for this project for the same reason the wheel
 * picker was: the registry item is Tailwind v4 (`data-slot` variants, `@container`,
 * `inset-ring-*`) and we are on v3 with themed tokens, so the same look is spelled
 * with the arbitrary variants v3 has.
 *
 * The box owns the border and the focus ring — `InputGroupInput` has neither, so a
 * group never shows a ring inside a ring. Everything is sized off the `Input`
 * component's own height, so a group sits in a form row beside a plain input
 * without a seam.
 */
const InputGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="input-group"
      className={cn(
        'flex h-10 w-full items-center gap-1 rounded-md border border-input bg-background px-2 text-sm',
        // The ring belongs to the box, raised by whatever inside it has focus.
        'ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50',
        'aria-[invalid=true]:border-destructive',
        className
      )}
      {...props}
    />
  )
);
InputGroup.displayName = 'InputGroup';

/**
 * The field itself: borderless and ringless, because the box around it has both.
 */
const InputGroupInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      data-slot="input-group-input"
      className={cn(
        'h-full min-w-0 flex-1 bg-transparent py-0 text-sm outline-none',
        'placeholder:text-muted-foreground disabled:cursor-not-allowed',
        // A number input's spinners are a second way to change the value that
        // nobody uses and that costs the field a third of its width.
        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        className
      )}
      {...props}
    />
  )
);
InputGroupInput.displayName = 'InputGroupInput';

export interface InputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Which end of the field it sits at. */
  align?: 'inline-start' | 'inline-end';
}

/**
 * Something attached to one end of the field. Clicking the padding around it
 * puts the cursor in the field, so the dead space between a unit and the number
 * is not dead.
 */
const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
  ({ className, align = 'inline-start', ...props }, ref) => (
    <div
      ref={ref}
      data-slot="input-group-addon"
      data-align={align}
      className={cn(
        'flex shrink-0 items-center gap-1 text-muted-foreground',
        align === 'inline-end' ? 'order-last' : 'order-first',
        className
      )}
      {...props}
    />
  )
);
InputGroupAddon.displayName = 'InputGroupAddon';

/** A word or a unit inside an addon — never something to click. */
const InputGroupText = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      data-slot="input-group-text"
      className={cn('select-none text-sm tabular-nums', className)}
      {...props}
    />
  )
);
InputGroupText.displayName = 'InputGroupText';

/**
 * A button inside the box, sized to live in it. `aria-pressed` is what makes one
 * a toggle — the "+" on a holds field is exactly that — and a pressed one takes
 * the accent so it reads as on at a glance.
 */
const InputGroupButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      data-slot="input-group-button"
      className={cn(
        'inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded px-1.5 text-sm font-medium',
        'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        'aria-[pressed=true]:bg-primary aria-[pressed=true]:text-primary-foreground',
        'aria-[pressed=true]:hover:bg-primary/90 aria-[pressed=true]:hover:text-primary-foreground',
        className
      )}
      {...props}
    />
  )
);
InputGroupButton.displayName = 'InputGroupButton';

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText };
